import { EmployerSignupStatus } from '@zorro/clients';
import { Alert } from '@zorro/design-system/v2/Alert';
import { Stack, Text } from '@zorro/web/design-system';

import {
  CompanyDetailsFormInputs,
  useCompanyDetailsForm,
} from '../../CompanyDetailsForm';
import {
  AddEmployerStep,
  AddEmployerStepType,
  HandleStepSubmit,
} from '../AddEmployerStep';

type Props = {
  employerId?: string;
  explicitEmployerStatus: EmployerSignupStatus | undefined;
  isFinalizationMode?: boolean;
  isDon?: boolean;
};

export const AddEmployerCompanyDetailsStep = ({
  employerId,
  explicitEmployerStatus,
  isFinalizationMode = false,
  isDon = false,
}: Props) => {
  const isSignedAgreementVisible = !isDon;

  const {
    companyDetailsForm,
    onSubmit,
    setHasLogoBeenDeleted,
    hasLogoBeenDeleted,
    isSignedAgreementRequired,
    isLynxPaymentsEnabled,
  } = useCompanyDetailsForm({
    employerId,
    isFinalizationMode,
    isSignedAgreementVisible,
    isDon,
    explicitEmployerStatus,
  });

  const title = isDon
    ? 'Check your company details and add any missing info'
    : 'Add company details';

  const handleStepSubmit: HandleStepSubmit = async () => {
    const result = await onSubmit();
    if (result) {
      return { navigate: true, employerId: result.employerId };
    }

    return { navigate: false };
  };

  const isSubmitDisabled = !companyDetailsForm.formState.isValid;
  const isNaicsCodeVisible = true;

  return (
    <AddEmployerStep
      isFinalizationMode={isFinalizationMode}
      step={AddEmployerStepType.COMPANY_DETAILS}
      isSubmitDisabled={isSubmitDisabled}
      onSubmit={handleStepSubmit}
      title={title}
      employerId={employerId}
      secondaryLabel="Skip"
      isDon={isDon}
    >
      <Alert variant="intro">
        <Stack gap="xs" pl="xs">
          <Text fw={600}>Set up a new employer in a few steps </Text>
          <Text fw={400} size="sm" lh="lg">
            1. Enter initial employer information <br />
            2. Invite the employer to review and add missing details <br />
            3. Monitor the employer in Zorro to finalize setup once the employer
            completes their part
          </Text>
        </Stack>
      </Alert>
      <CompanyDetailsFormInputs
        companyDetailsForm={companyDetailsForm}
        isFinalizationMode={isFinalizationMode}
        setHasLogoBeenDeleted={setHasLogoBeenDeleted}
        hasLogoBeenDeleted={hasLogoBeenDeleted}
        isSignedAgreementRequired={isSignedAgreementRequired}
        isSignedAgreementVisible={isSignedAgreementVisible}
        isNaicsCodeVisible={isNaicsCodeVisible}
        isLynxPaymentsEnabled={isLynxPaymentsEnabled}
      />
    </AddEmployerStep>
  );
};
